/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useRef, useEffect } from "react";
import { FiUser, FiShoppingCart, FiSearch, FiLogOut, FiPackage, FiX, FiMenu, FiPhone } from "react-icons/fi";
import { buildImageUrl, publicStoreText } from "../theme1/utils/theme1Helpers";

const Header = ({
  theme,
  storeData,
  cartCount,
  onCartClick,
  onUserClick,
  searchQuery,
  onSearchChange,
  customer,
  onLogout,
  onOrdersClick,
}) => {
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowAccountMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const slogan = publicStoreText(storeData?.storeSlogan);

  const scrollTo = (id) => {
    setMobileOpen(false);
    if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const navItems = [
    { label: "الرئيسية", id: "top" },
    { label: "المنتجات", id: "all-products" },
    { label: "الأقسام", id: "categories-section" },
    { label: "تواصل معنا", id: "contact-section" },
  ];

  return (
    <header className="t3-header" dir="rtl">
      <div className="t3-topbar" style={{ background: theme.primary }}>
        <div className="t3-container t3-topbar-inner">
          <span>مرحباً بكم في {storeData?.storeName || "المتجر الإلكتروني"}</span>
          {storeData?.phone && (
            <a href={`tel:${storeData.phone}`} className="t3-topbar-phone">
              <FiPhone /> {storeData.phone}
            </a>
          )}
        </div>
      </div>

      <nav className="t3-navbar">
        <div className="t3-container t3-navbar-inner">
          <button type="button" className="t3-menu-btn" onClick={() => setMobileOpen((v) => !v)} aria-label="القائمة">
            {mobileOpen ? <FiX /> : <FiMenu />}
          </button>

          <button type="button" className="t3-logo" onClick={() => scrollTo("top")}>
            {storeData?.logo ? (
              <img src={buildImageUrl(storeData.logo)} alt={storeData.storeName || "logo"} />
            ) : (
              <span className="t3-logo-fallback" style={{ background: theme.primary }}>
                {(storeData?.storeName || "م").charAt(0)}
              </span>
            )}
            <div>
              <strong style={{ color: theme.primary }}>{storeData?.storeName || "المتجر"}</strong>
              {slogan && <small>{slogan}</small>}
            </div>
          </button>

          <div className="t3-nav-links">
            {navItems.map((item) => (
              <button key={item.id} type="button" onClick={() => scrollTo(item.id)}>
                {item.label}
              </button>
            ))}
          </div>

          <div className="t3-search">
            <FiSearch />
            <input
              type="text"
              placeholder="ابحث عن منتج..."
              value={searchQuery}
              onChange={onSearchChange}
            />
            {searchQuery && (
              <button type="button" className="t3-search-clear" onClick={() => onSearchChange({ target: { value: "" } })}>
                <FiX />
              </button>
            )}
          </div>

          <div className="t3-header-actions">
            {customer?.customerId ? (
              <div className="t3-account-wrap" ref={menuRef}>
                <button type="button" className="t3-icon-btn" onClick={() => setShowAccountMenu((v) => !v)}>
                  <FiUser />
                </button>
                {showAccountMenu && (
                  <div className="t3-account-menu">
                    <div className="t3-account-head">
                      <p>{customer.name || customer.customerName}</p>
                      {customer.phone && <span dir="ltr">{customer.phone}</span>}
                    </div>
                    <button type="button" onClick={() => { onOrdersClick?.(); setShowAccountMenu(false); }}>
                      <FiPackage /> طلباتي
                    </button>
                    <button type="button" className="is-danger" onClick={() => { onLogout?.(); setShowAccountMenu(false); }}>
                      <FiLogOut /> تسجيل الخروج
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button type="button" className="t3-icon-btn" onClick={onUserClick} title="حسابي">
                <FiUser />
              </button>
            )}

            <button type="button" className="t3-cart-btn" onClick={onCartClick} style={{ background: theme.primary }}>
              <FiShoppingCart />
              {cartCount > 0 && <span>{cartCount}</span>}
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="t3-mobile-nav">
          {navItems.map((item) => (
            <button key={item.id} type="button" onClick={() => scrollTo(item.id)}>
              {item.label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
};

export default Header;
